import styled from "@emotion/styled";
import { Box } from "@mui/material";
import { shouldForwardProp } from "./Common.style";
import { forwardRef, useEffect, useImperativeHandle, useRef } from "react";

type StyleBoxProps = {
  isLoading?: boolean;
  restTop?: number;
};

export const ScrollListContainerStyle = styled(Box, {
  shouldForwardProp: prop =>
    shouldForwardProp<StyleBoxProps>(["isLoading", "restTop"], prop),
})<StyleBoxProps>(({ restTop }) => ({
  overflowY: restTop ? "auto" : "visible",
  overflowAnchor: "auto",
  display: "block",
  maxHeight: restTop ? `calc(100dvh - ${restTop}px)` : "100dvh",
  position: "relative",
  flexShrink: 1,
  height: "100%",
}));

interface ScrollListContainerType {
  children: React.JSX.Element | React.JSX.Element[];
  isLoading?: boolean;
  onScrollCallback?: () => Promise<void>;
  dataTestId: string;
  restTop?: number;
  resetTrigger?: any;
}

export interface ScrollListContainerHandle {
  getScrollContainer: () => HTMLDivElement | null;
}

const ScrollListContainer = forwardRef<
  ScrollListContainerHandle,
  ScrollListContainerType
>(
  (
    {
      children,
      isLoading = false,
      onScrollCallback,
      dataTestId,
      restTop = 200,
      resetTrigger,
    },
    ref,
  ) => {
    const scrollContainerRef = useRef<HTMLDivElement>(null);

    useImperativeHandle(ref, () => ({
      getScrollContainer: () => scrollContainerRef.current,
    }));

    useEffect(() => {
      if (scrollContainerRef?.current) {
        scrollContainerRef.current.scrollTop = 0;
      }
    }, [resetTrigger]);

    const onScroll = async (event: any) => {
      if (isLoading) return;
      if (
        onScrollCallback &&
        event.target.offsetHeight + event.target.scrollTop >=
          event.target.scrollHeight - 200
      ) {
        await onScrollCallback();
      }
    };
    return (
      <ScrollListContainerStyle
        onScroll={onScroll}
        isLoading={isLoading}
        data-testid={dataTestId}
        ref={scrollContainerRef}
        restTop={restTop}
      >
        {children}
      </ScrollListContainerStyle>
    );
  },
);

export default ScrollListContainer;
